import { useState, useEffect } from "react";
import { MapPin, Phone, Globe, Clock, Car, ExternalLink, ChevronLeft, ChevronRight, X, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Static hero image - locked and not affected by gallery uploads
const HERO_IMAGE = {
  url: "https://images.pexels.com/photos/31803613/pexels-photo-31803613.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  caption: "Royal Woodbine Golf Club - Championship Course"
};

// Default images as fallback when no custom images are uploaded to the gallery
const defaultGalleryImages = [
  {
    url: "https://images.unsplash.com/photo-1587174486073-ae5e5cff23aa?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2",
    caption: "Championship Fairway"
  },
  {
    url: "https://images.unsplash.com/photo-1535131749006-b7f58c99034b?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2",
    caption: "Scenic Course View"
  },
  {
    url: "https://images.unsplash.com/photo-1592919505780-303950717480?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2",
    caption: "Golf at Sunset"
  }
];

export default function VenuePage() {
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [galleryImages, setGalleryImages] = useState(defaultGalleryImages);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchGalleryImages();
  }, []);

  const fetchGalleryImages = async () => {
    try {
      const res = await axios.get(`${API}/gallery`);
      if (res.data && res.data.length > 0) {
        // Only use API images for the gallery section, not the hero
        setGalleryImages(res.data);
      }
      // If no images in API, keep the default gallery images
    } catch (err) {
      console.error("Failed to fetch gallery images:", err);
      // Keep default images on error
    } finally {
      setLoading(false);
    }
  };

  const openLightbox = (index) => {
    setCurrentIndex(index);
    setSelectedImage(galleryImages[index]);
  };

  const closeLightbox = () => {
    setSelectedImage(null);
  };

  const nextImage = () => {
    const newIndex = (currentIndex + 1) % galleryImages.length;
    setCurrentIndex(newIndex);
    setSelectedImage(galleryImages[newIndex]);
  };

  const prevImage = () => {
    const newIndex = (currentIndex - 1 + galleryImages.length) % galleryImages.length;
    setCurrentIndex(newIndex);
    setSelectedImage(galleryImages[newIndex]);
  };

  return (
    <div className="min-h-screen bg-[#F9F8F4] py-12 md:py-20">
      <div className="max-w-6xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-12">
          <p className="text-xs font-bold uppercase tracking-widest text-[#666055] mb-3">
            Our Venue
          </p>
          <h1 className="text-4xl md:text-5xl tracking-tight text-[#1C1C1C] mb-6" data-testid="venue-title">
            Royal Woodbine Golf Club
          </h1>
          <p className="text-lg text-[#666055] max-w-3xl mx-auto leading-relaxed">
            Experience one of Toronto's premier golf destinations, offering a championship-caliber course 
            with stunning views and world-class facilities.
          </p>
        </div>

        {/* Hero Image - LOCKED, not affected by gallery uploads */}
        <div className="relative rounded-3xl overflow-hidden mb-12 aspect-[21/9]">
          <img 
            src={HERO_IMAGE.url}
            alt="Royal Woodbine Golf Club"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
          <div className="absolute bottom-6 left-6 text-white">
            <h2 className="text-2xl md:text-3xl font-light mb-2">Championship Course</h2>
            <p className="text-white/80">Host of the 2026 BISN Charity Golf Classic</p>
          </div>
        </div>

        {/* Venue Details Grid */}
        <div className="grid md:grid-cols-2 gap-8 mb-16">
          {/* Description */}
          <div className="bg-white rounded-2xl border border-[#E6E2D6] p-8">
            <h3 className="text-2xl text-[#1C1C1C] mb-4">About the Venue</h3>
            <div className="space-y-4 text-[#666055] leading-relaxed">
              <p>
                Royal Woodbine Golf Club is a prestigious 18-hole championship golf course located in 
                the heart of Etobicoke, Toronto. Known for its meticulously maintained fairways, 
                challenging layout, and stunning natural surroundings, it provides the perfect backdrop 
                for our charity tournament.
              </p>
              <p>
                The course features rolling terrain, strategic bunkering, and water hazards that will 
                test golfers of all skill levels while offering an enjoyable experience for everyone.
              </p>
              <p>
                The clubhouse offers elegant facilities including a fully-equipped pro shop, comfortable 
                locker rooms, and a beautiful banquet hall where our awards ceremony and 19th Hole 
                Happy Hour will take place.
              </p>
            </div>
          </div>

          {/* Quick Info */}
          <div className="space-y-6">
            {/* Location Card */}
            <a 
              href="https://www.google.com/maps/place/Royal+Woodbine+Golf+Club/@43.6752,-79.5847,15z"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white rounded-2xl border border-[#E6E2D6] p-6 flex items-start gap-4 hover:shadow-lg transition-all duration-300 hover:border-[#3A5F40] group block"
              data-testid="venue-map-link"
            >
              <div className="w-12 h-12 rounded-full bg-[#3A5F40]/10 flex items-center justify-center flex-shrink-0 group-hover:bg-[#3A5F40]/20">
                <MapPin size={24} className="text-[#3A5F40]" />
              </div>
              <div className="flex-1">
                <h4 className="font-medium text-[#1C1C1C] mb-1">Address</h4>
                <p className="text-[#666055]">195 Galaxy Blvd</p>
                <p className="text-[#666055]">Toronto (Etobicoke), Ontario</p>
                <p className="text-[#666055]">M9W 6R7</p>
                <p className="text-sm text-[#3A5F40] mt-2 group-hover:underline flex items-center gap-1">
                  Get Directions <ExternalLink size={14} />
                </p>
              </div>
            </a>

            {/* Contact & Hours */}
            <div className="bg-white rounded-2xl border border-[#E6E2D6] p-6">
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-[#C5A059]/10 flex items-center justify-center flex-shrink-0">
                    <Phone size={20} className="text-[#C5A059]" />
                  </div>
                  <div>
                    <h4 className="font-medium text-[#1C1C1C] mb-1">Contact</h4>
                    <p className="text-[#666055]">(416) 674-4653</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-[#C5A059]/10 flex items-center justify-center flex-shrink-0">
                    <Globe size={20} className="text-[#C5A059]" />
                  </div>
                  <div>
                    <h4 className="font-medium text-[#1C1C1C] mb-1">Website</h4>
                    <a 
                      href="https://www.royalwoodbine.com" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-[#3A5F40] hover:underline"
                    >
                      www.royalwoodbine.com
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-[#C5A059]/10 flex items-center justify-center flex-shrink-0">
                    <Car size={20} className="text-[#C5A059]" />
                  </div>
                  <div>
                    <h4 className="font-medium text-[#1C1C1C] mb-1">Parking</h4>
                    <p className="text-[#666055]">Complimentary parking available</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Photo Gallery - Only shows images from the Photo Gallery in Admin */}
        <div className="mb-16">
          <h3 className="text-2xl text-[#1C1C1C] text-center mb-8">Photo Gallery</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {galleryImages.map((image, index) => (
              <button
                key={index}
                onClick={() => openLightbox(index)}
                className="relative aspect-square rounded-xl overflow-hidden group cursor-pointer"
                data-testid={`gallery-image-${index}`}
              >
                <img 
                  src={image.url}
                  alt={image.caption}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors duration-300" />
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <span className="text-white text-sm font-medium">View</span>
                </div>
              </button>
            ))}
          </div>
          <p className="text-center text-sm text-[#666055] mt-4">
            Click on any image to view larger
          </p>
        </div>

        {/* CTA */}
        <div className="text-center">
          <a 
            href="/register"
            className="inline-flex items-center justify-center gap-2 bg-[#3A5F40] hover:bg-[#2A4430] text-white rounded-full px-8 py-4 text-lg font-medium transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-1"
            data-testid="venue-register-btn"
          >
            Register for the Tournament
          </a>
        </div>
      </div>

      {/* Lightbox Modal */}
      {selectedImage && (
        <div 
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={closeLightbox}
        >
          <button 
            className="absolute top-4 right-4 text-white/80 hover:text-white p-2"
            onClick={closeLightbox}
            data-testid="lightbox-close"
          >
            <X size={32} />
          </button>
          
          <button 
            className="absolute left-4 top-1/2 -translate-y-1/2 text-white/80 hover:text-white p-2 bg-black/30 rounded-full"
            onClick={(e) => { e.stopPropagation(); prevImage(); }}
            data-testid="lightbox-prev"
          >
            <ChevronLeft size={32} />
          </button>
          
          <div className="max-w-5xl max-h-[80vh]" onClick={(e) => e.stopPropagation()}>
            <img 
              src={selectedImage.url}
              alt={selectedImage.caption}
              className="max-w-full max-h-[75vh] object-contain rounded-lg"
            />
            <p className="text-white text-center mt-4">{selectedImage.caption}</p>
            <p className="text-white/60 text-center text-sm mt-1">
              {currentIndex + 1} of {galleryImages.length}
            </p>
          </div>
          
          <button 
            className="absolute right-4 top-1/2 -translate-y-1/2 text-white/80 hover:text-white p-2 bg-black/30 rounded-full"
            onClick={(e) => { e.stopPropagation(); nextImage(); }}
            data-testid="lightbox-next"
          >
            <ChevronRight size={32} />
          </button>
        </div>
      )}
    </div>
  );
}
